import { useCallback, useSyncExternalStore } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConsentState = {
  essential: true; // always true — strictly necessary
  analytics: boolean; // controls Sentry, attribution tracking, vidi_aid cookie
};

type StoredConsent = ConsentState & {
  decidedAt: string; // ISO timestamp for audit trail
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CONSENT_KEY = 'vidi-consent';

// ---------------------------------------------------------------------------
// Pure helpers (usable outside React — e.g. in attribution.ts, main.tsx)
// ---------------------------------------------------------------------------

/** Read raw consent from localStorage (returns null if undecided). */
export function readConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredConsent;
  } catch {
    return null;
  }
}

/** Check whether the user has granted analytics consent. */
export function hasAnalyticsConsent(): boolean {
  const consent = readConsent();
  return consent?.analytics === true;
}

// ---------------------------------------------------------------------------
// External-store plumbing so every useConsent() subscriber re-renders
// when consent changes (even from another tab via the "storage" event).
// ---------------------------------------------------------------------------

let listeners: Array<() => void> = [];

function subscribe(listener: () => void) {
  listeners = [...listeners, listener];

  // Also listen for cross-tab changes
  const onStorage = (e: StorageEvent) => {
    if (e.key === CONSENT_KEY) listener();
  };
  window.addEventListener('storage', onStorage);

  return () => {
    listeners = listeners.filter((l) => l !== listener);
    window.removeEventListener('storage', onStorage);
  };
}

function getSnapshot(): string | null {
  return localStorage.getItem(CONSENT_KEY);
}

function emitChange() {
  for (const listener of listeners) {
    listener();
  }
}

// ---------------------------------------------------------------------------
// Write helper (fires listeners so hook consumers re-render)
// ---------------------------------------------------------------------------

function writeConsent(state: ConsentState): void {
  const stored: StoredConsent = {
    ...state,
    decidedAt: new Date().toISOString(),
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(stored));
  emitChange();
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useConsent() {
  const raw = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  const consent: ConsentState | null = (() => {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as StoredConsent;
      return { essential: parsed.essential, analytics: parsed.analytics };
    } catch {
      return null;
    }
  })();

  const hasDecided = consent !== null;

  const acceptAll = useCallback(() => {
    writeConsent({ essential: true, analytics: true });
  }, []);

  const acceptEssential = useCallback(() => {
    writeConsent({ essential: true, analytics: false });
  }, []);

  const updateConsent = useCallback((updates: Partial<ConsentState>) => {
    const current = readConsent();
    const base: ConsentState = current
      ? { essential: true, analytics: current.analytics }
      : { essential: true, analytics: false };
    writeConsent({ ...base, ...updates, essential: true });
  }, []);

  return { consent, hasDecided, acceptAll, acceptEssential, updateConsent } as const;
}
